// api/config.ts
import { triggerUnauthorized } from '../utils/navigation';
import { resolveApiBaseUrl } from './runtime-config';

// Resolved in the browser at load time, not inlined by webpack at build time —
// so one image serves kivo.skin and dev.kivo.skin against the right API.
// See runtime-config.ts for the precedence chain.
export const API_BASE_URL = resolveApiBaseUrl();

const TOKEN_KEY = 'auth_token';

interface JwtPayload {
  exp?: number;
}

export class LoginAuthError extends Error {
  status: 401 | 403;
  code: 'EMAIL_NOT_VERIFIED' | 'WRONG_PASSWORD';
  email?: string;
  raw?: unknown;
  constructor(
    status: 401 | 403,
    code: LoginAuthError['code'],
    payload?: unknown,
  ) {
    super(code === 'EMAIL_NOT_VERIFIED' ? 'Email not verified' : 'Wrong password');
    this.name = 'LoginAuthError';
    this.status = status;
    this.code = code;
    if (isRecord(payload)) {
      this.email =
        typeof payload.Email === 'string'
          ? payload.Email
          : typeof payload.email === 'string'
            ? payload.email
            : undefined;
    }
    this.raw = payload;
  }
}

/**
 * Tagged error for the runtime auth kill-switch: the backend can close
 * registrations or disable logins independently of any per-user state.
 * Distinct from LoginAuthError (which is about THIS user's credentials/
 * verification status) so pages can show a calm "temporarily unavailable"
 * message instead of a generic auth failure.
 */
export class AuthGateError extends Error {
  status: 403;
  code: 'REGISTRATIONS_CLOSED' | 'LOGINS_DISABLED';
  raw?: unknown;
  constructor(status: 403, code: AuthGateError['code'], payload?: unknown) {
    super(code === 'REGISTRATIONS_CLOSED' ? 'Registrations closed' : 'Logins disabled');
    this.name = 'AuthGateError';
    this.status = status;
    this.code = code;
    this.raw = payload;
  }
}

export class VerificationExpiredError extends Error {
  readonly code = 410;
  constructor(message = 'Verification link expired') {
    super(message);
    this.name = 'VerificationExpiredError';
  }
}

export type ApiFetchInit = RequestInit & { binary?: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getPayloadMessage(payload: unknown): string {
  if (!isRecord(payload)) return '';
  const error = payload.error;
  const message = payload.message;
  if (typeof error === 'string') return error;
  if (typeof message === 'string') return message;
  return '';
}

function toErrorPayload(payload: unknown, fallback: string): Error {
  if (payload instanceof Error) return payload;
  const message = getPayloadMessage(payload);
  return new Error(message || fallback);
}

/**
 * Non-OK API answer (other than the 401/402/403 special paths). Carries the
 * HTTP status and the parsed JSON body (or null) so flows can branch on
 * structured answers (409 `ingredients_pending`, 422 scan bodies, …). The
 * message is the server's `error`/`message` text, as before, so callers that
 * only read `err.message` keep working.
 */
export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

/** True when a thrown value is a typed non-OK API answer. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// --- Defaults
export const DEFAULT_HEADERS: Record<string, string> = {
  Accept: 'application/json',
  // Signal the client platform so the backend can gate affiliate monetization
  // and the disclosure flag per platform (web | ios | android).
  'X-Client-Platform': 'web',
};

// --- Telemetry helpers
export function getAppVersion(): string {
  return process.env.REACT_APP_VERSION ?? 'unknown';
}

export const ORIGIN = 'web' as const;

// --- Token helpers
export function getAuthToken(): string | null {
  return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
}
export function setAuthToken(token: string): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(TOKEN_KEY, token);
}
export function clearAuthToken(): void {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(TOKEN_KEY);
}

/** Base64url→JSON decode, return true if exp is past (with small skew) */
export function isTokenExpired(token: string, skewSec = 30): boolean {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return true;
    const json = JSON.parse(
      atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
    ) as JwtPayload;
    if (!json.exp) return false; // no exp => treat as non-expiring
    const now = Math.floor(Date.now() / 1000);
    return json.exp < now + skewSec;
  } catch {
    return true; // malformed => treat as expired
  }
}

/** Build headers for a request, injecting Authorization when a valid token exists */
export function buildHeaders(isBinary?: boolean): Record<string, string> {
  const headers: Record<string, string> = { ...DEFAULT_HEADERS };

  // Only set JSON Content-Type when sending a JSON body (apiFetch will add it)
  if (isBinary) headers['Content-Type'] = 'application/octet-stream';

  const token = getAuthToken();
  if (token && !isTokenExpired(token)) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Establish the browser media session: stores the auth JWT in an httpOnly
 * cookie so <img> requests to ownership-checked image endpoints (which cannot
 * send an Authorization header) are authenticated. Idempotent and non-fatal.
 */
export async function establishMediaSession(): Promise<void> {
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) return;
  try {
    await fetch(`${API_BASE_URL}auth/web-session`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* non-fatal: images simply won't load until the session is established */
  }
}

// --- Credit-economy error messages (shared across endpoints) ---
export const INSUFFICIENT_CREDITS_MESSAGE =
  'Insufficient credits — top up or wait for your monthly reset';

/**
 * Window event fired whenever the API rejects a metered action with 402.
 * A top-level host listens for it and opens the credit top-up popup.
 * `detail` carries `{ path, payload }` from the failing request.
 */
export const INSUFFICIENT_CREDITS_EVENT = 'kivo:insufficient-credits';
export const FEATURE_LOCKED_MESSAGE = 'Upgrade your plan to use this feature';

/**
 * Tagged error for credit/feature gating so callers can branch UX
 * (e.g. show a "Buy credits" CTA vs an "Upgrade" CTA).
 */
export class CreditError extends Error {
  status: 402 | 403;
  kind: 'insufficient_credits' | 'feature_locked';
  payload: unknown;
  constructor(status: 402 | 403, payload?: unknown) {
    const kind = status === 402 ? 'insufficient_credits' : 'feature_locked';
    const message =
      status === 402 ? INSUFFICIENT_CREDITS_MESSAGE : FEATURE_LOCKED_MESSAGE;
    super(message);
    this.name = 'CreditError';
    this.status = status;
    this.kind = kind;
    this.payload = payload ?? null;
  }
}

/** True when a thrown value is a credit/feature gating error. */
export function isCreditError(err: unknown): err is CreditError {
  return err instanceof CreditError;
}

/**
 * Heuristic: does a 403 payload represent a feature/tier lock rather than an
 * authentication failure? The backend returns 403 for tier-gated features
 * (premium face, hair, makeup); those must not log the user out.
 */
function isFeatureLockPayload(payload: unknown, _path: string): boolean {
  const msg = getPayloadMessage(payload).toLowerCase();
  if (!msg) return false;
  // Never treat auth/session errors as feature locks.
  if (
    msg.includes('unauthor') ||
    msg.includes('token') ||
    msg.includes('banned') ||
    msg.includes('not verified') ||
    msg.includes('email_not_verified') ||
    msg.includes('forbidden origin')
  ) {
    return false;
  }
  return (
    msg.includes('plan') ||
    msg.includes('tier') ||
    msg.includes('upgrade') ||
    msg.includes('plus') ||
    msg.includes('premium') ||
    msg.includes('feature') ||
    msg.includes('subscription')
  );
}

function buildLoginAuthError(
  status: 401 | 403,
  code: LoginAuthError['code'],
  payload?: unknown,
): LoginAuthError {
  return new LoginAuthError(status, code, payload);
}

async function readJsonPayload(response: Response): Promise<unknown> {
  try {
    return await response.clone().json() as unknown;
  } catch {
    return null;
  }
}

// prevent multi-redirect storms across rapid 401s
let _redirecting401 = false;

/**
 * Thin fetch wrapper:
 * - Adds base URL and auth header
 * - Normalizes login-specific auth errors so the page can branch UX
 * - Falls back to global unauthorized handling for other 401/403s
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  const { binary, headers, ...rest } = init;

  // Normalize path and detect login/auth calls early
  const normalizedPath = path.replace(/^\//, '');
  const isLoginCall = normalizedPath.startsWith('auth/login');
  const isRegisterCall = normalizedPath.startsWith('auth/register');

  // Short-circuit if token exists but is already expired
  // Skip this check for login/register as these are how you GET a token
  const currentToken = getAuthToken();
  if (currentToken && isTokenExpired(currentToken) && !isLoginCall && !isRegisterCall) {
    clearAuthToken();
    if (!_redirecting401 && typeof window !== 'undefined') {
      _redirecting401 = true;
      triggerUnauthorized();
    }
    throw new Error('Token expired');
  }

  const mergedHeaders = new Headers(buildHeaders(binary));
  if (headers) {
    for (const [key, value] of new Headers(headers).entries()) {
      mergedHeaders.set(key, value);
    }
  }

  // If we have a non-binary body and no Content-Type yet, assume JSON.
  // FormData is the exception: the browser must set the multipart boundary.
  const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData;
  if (!binary && rest.body && !isFormData && !mergedHeaders.has('Content-Type')) {
    mergedHeaders.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
    ...rest,
    headers: mergedHeaders,
    // Send/receive the httpOnly media-session cookie so <img> requests to
    // ownership-checked image endpoints are authenticated.
    credentials: 'include',
  });

  // Credit economy: 402 = insufficient credits (distinct from 429 rate-limit).
  if (response.status === 402) {
    const payload = await readJsonPayload(response);
    reportApiError(normalizedPath, 402, INSUFFICIENT_CREDITS_MESSAGE);
    // Surface a global "out of credits" signal so the app can present a
    // top-up popup regardless of which call site triggered the 402.
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent(INSUFFICIENT_CREDITS_EVENT, {
            detail: { path: normalizedPath, payload },
          }),
        );
      } catch {
        /* CustomEvent unsupported — non-fatal */
      }
    }
    throw new CreditError(402, payload);
  }

  // Handle 401/403 first to keep semantics tight
  if (response.status === 401 || response.status === 403) {
    const payload = await readJsonPayload(response);

    if (response.status === 403 && isRecord(payload) && payload.error === 'User is banned') {
      clearAuthToken();
      if (typeof window !== 'undefined') {
        window.location.href = '/login?banned=true';
      }
      throw new Error('User is banned');
    }

    // Auth kill-switch: backend closed registrations or disabled logins.
    // Checked before the feature-lock heuristic (and before the login/global
    // fallbacks below) so these structured, machine-readable codes always
    // win over message-text guessing. Not an auth failure for THIS user, so
    // never clear the token / trigger the global unauthorized redirect.
    if (response.status === 403 && isRecord(payload)) {
      const gateCode = payload.ErrorString ?? payload.error;
      if (isRegisterCall && gateCode === 'registrations_closed') {
        throw new AuthGateError(403, 'REGISTRATIONS_CLOSED', payload);
      }
      if (isLoginCall && gateCode === 'logins_disabled') {
        throw new AuthGateError(403, 'LOGINS_DISABLED', payload);
      }
    }

    // Feature/tier lock (e.g. premium face, hair, makeup require Plus/Premium).
    // These 403s are NOT auth failures: keep the session and surface an upgrade CTA.
    if (response.status === 403 && isFeatureLockPayload(payload, normalizedPath)) {
      reportApiError(normalizedPath, 403, FEATURE_LOCKED_MESSAGE);
      throw new CreditError(403, payload);
    }

    // --- Special-case: LOGIN behaviors ---
    if (isLoginCall) {
      // 403: Email not verified -> let LoginPage handle redirect to pending screen
      if (
        response.status === 403 &&
        isRecord(payload) &&
        (payload.ErrorString === 'email_not_verified' || payload.error === 'email_not_verified')
      ) {
        throw buildLoginAuthError(403, 'EMAIL_NOT_VERIFIED', payload);
      }

      // 401: Wrong password -> let LoginPage show "Invalid credentials"
      if (response.status === 401) {
        throw buildLoginAuthError(401, 'WRONG_PASSWORD', payload);
      }
      // For any other unexpected login 403, fall through to global handler below
    }

    // --- Global unauthorized: clear token and route to login ---
    clearAuthToken();
    if (!_redirecting401 && typeof window !== 'undefined') {
      _redirecting401 = true;
      triggerUnauthorized();
    }
    // Re-throw payload if available so callers can still inspect
    if (payload) throw toErrorPayload(payload, 'Unauthorized');
    throw new Error('Unauthorized');
  }

  // Other non-OK statuses -> bubble JSON error when possible
  if (!response.ok) {
    const ct = response.headers.get('content-type') ?? '';
    let errorPayload: unknown = null;
    if (ct.includes('application/json')) {
      try {
        errorPayload = await response.json();
      } catch {
        errorPayload = null;
      }
    }
    const message = getPayloadMessage(errorPayload) || response.statusText;

    // Report non-auth API errors to telemetry
    reportApiError(normalizedPath, response.status, message);

    throw new ApiError(response.status, message, errorPayload);
  }

  // Handle 204 / empty bodies gracefully
  if (response.status === 204) return null as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }
  return response as T;
}

// Helper to report API errors to telemetry (called externally if needed)
let _lastErrorKey: string | null = null;
let _lastErrorTime = 0;
const ERROR_DEDUPE_MS = 60000; // 60s window

export function reportApiError(endpoint: string, statusCode: number, message: string) {
  // Dedupe identical errors
  const key = `${endpoint}:${statusCode}:${message}`;
  const now = Date.now();
  if (key === _lastErrorKey && now - _lastErrorTime < ERROR_DEDUPE_MS) {
    return; // skip duplicate
  }
  _lastErrorKey = key;
  _lastErrorTime = now;

  // Don't report auth/telemetry errors to avoid recursion
  if (endpoint.includes('auth/') || endpoint.includes('telemetry/')) {
    return;
  }

  // Lazy import to avoid circular deps
  void import('./telemetry')
    .then(({ postError }) => {
      void postError(`API_${statusCode}`, message, endpoint);
    })
    .catch(() => {
      /* telemetry unavailable */
    });
}
